import React, { useState, useEffect } from 'react';
import {
    Box, TextField, Typography
} from '@mui/material';
import { Departamento } from '../../types/negocio/Departamento';
import BasicModal from './BasicModal';

interface Props {
    open: boolean;
    onClose: () => void;
    onSave?: (data: Departamento) => void;
    departamento: Departamento;
    modoEdicao?: boolean;
    setDepartamento?: React.Dispatch<React.SetStateAction<Departamento>>;
    onEditClick?: () => void;
    isNovoDepartamento?: boolean;
}

const DepartamentoModal: React.FC<Props> = ({
    open,
    onClose,
    onSave,
    departamento,
    modoEdicao = false,
    setDepartamento,
    onEditClick,
    isNovoDepartamento = false
}) => {
    const [errors, setErrors] = useState<Record<string, boolean>>({});

    // Lista de campos obrigatórios
    const camposObrigatorios = ['nome'];

    // Validar campos obrigatórios
    const validarCampos = (): boolean => {
        const novosErros: Record<string, boolean> = {};
        
        if (modoEdicao && isNovoDepartamento) {
            camposObrigatorios.forEach(campo => {
                const valor = departamento[campo as keyof Departamento];
                if (!valor || valor.toString().trim() === '') {
                    novosErros[campo] = true;
                }
            });
        }
        
        setErrors(novosErros);
        return Object.keys(novosErros).length === 0;
    };

    // Limpar erros quando o modal fechar
    useEffect(() => {
        if (!open) {
            setErrors({});
        }
    }, [open]);

    const getModalTitle = () => {
        if (modoEdicao) {
            return isNovoDepartamento ? "Adicionar Departamento" : "Editar Departamento";
        }
        return "Detalhes do Departamento";
    };

    const handleChange = (field: keyof Departamento, value: string) => {
        if (!setDepartamento) return;
        setDepartamento(prev => ({ ...prev, [field]: value }));
        
        // Limpar erro do campo quando ele for preenchido
        if (errors[field] && value.trim() !== '') {
            setErrors(prev => ({ ...prev, [field]: false }));
        }
    };

    const renderCampo = (label: string, field: keyof Departamento, multiline = false) => {
        const isObrigatorio = modoEdicao && isNovoDepartamento && camposObrigatorios.includes(field);
        const temErro = errors[field] || false;
        
        const valor = departamento[field];
        if (!modoEdicao && (!valor || valor.toString().trim() === '')) return null;

        return (
            <Box sx={{ width: '100%', px: 1, mb: 2 }}>
                {modoEdicao ? (
                    <TextField
                        fullWidth
                        label={label}
                        variant="outlined"
                        color="secondary"
                        value={valor || ''}
                        onChange={(e) => handleChange(field, e.target.value)}
                        multiline={multiline}
                        rows={multiline ? 3 : 1}
                        error={temErro}
                        helperText={temErro ? 'Este campo é obrigatório' : ''}
                        required={isObrigatorio}
                    />
                ) : (
                    <>
                        <Typography fontSize={12} sx={{ color: "#607C90" }}>
                            {label}
                        </Typography>
                        <Typography fontSize={16}>
                            {valor}
                        </Typography>
                    </>
                )}
            </Box>
        );
    };

    return (
        <BasicModal
            title={getModalTitle()}
            open={open}
            onClose={onClose}
            saveButton={modoEdicao}
            onSave={() => {
                if (modoEdicao && isNovoDepartamento) {
                    if (validarCampos()) {
                        onSave && onSave(departamento);
                    }
                } else {
                    onSave && onSave(departamento);
                }
            }}
            onEdit={onEditClick}
        >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 1 }}>
                {renderCampo('Nome', 'nome')}
                {renderCampo('Descrição', 'descricao', true)}
            </Box>
        </BasicModal>
    );
};

export default DepartamentoModal;